import React, { useState } from 'react';
import { ArrowLeft, Edit2, Trash2, Download, Upload, AlertTriangle } from 'lucide-react';
import {
  getProfiles,
  deleteProfile,
  exportProfileData,
  importProfileData
} from '../../utils/profileManager';
import { playSound } from '../../utils/soundManager';
import ProfileEditor from './ProfileEditor';

const ProfileManager = ({ onClose, onProfilesUpdated }) => {
  const [profiles, setProfiles] = useState(getProfiles());
  const [editingProfile, setEditingProfile] = useState(null);
  const [deletingProfile, setDeletingProfile] = useState(null);

  const loadProfiles = () => {
    const allProfiles = getProfiles();
    setProfiles(allProfiles);
    onProfilesUpdated();
  };

  const handleEdit = (profile) => {
    playSound('click');
    setEditingProfile(profile);
  };

  const handleDelete = (profile) => {
    playSound('click');
    setDeletingProfile(profile);
  };

  const confirmDelete = () => {
    playSound('success');
    deleteProfile(deletingProfile.id);
    setDeletingProfile(null);
    loadProfiles();
  };

  const cancelDelete = () => {
    playSound('click');
    setDeletingProfile(null);
  };

  const handleExport = (profile) => {
    try {
      const data = exportProfileData(profile.id);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${profile.name}-profile-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      playSound('success');
    } catch (error) {
      console.error('Export failed:', error);
      playSound('error');
    }
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      try {
        const file = e.target.files[0];
        const text = await file.text();
        importProfileData(text);
        playSound('success');
        loadProfiles();
      } catch (error) {
        console.error('Import failed:', error);
        playSound('error');
        alert('Failed to import profile. Please check the file and try again.');
      }
    };
    input.click();
  };

  const handleProfileUpdated = () => {
    setEditingProfile(null);
    loadProfiles();
  };

  if (editingProfile) {
    return (
      <ProfileEditor
        profile={editingProfile}
        onSave={handleProfileUpdated}
        onCancel={() => setEditingProfile(null)}
      />
    );
  }

  return (
    <div className="profile-manager-container">
      <div className="profile-manager-content animate-fadeIn">
        <div className="profile-manager-header">
          <button
            className="back-button hover-scale btn-press"
            onClick={onClose}
          >
            <ArrowLeft size={24} />
          </button>
          <h2>Manage Profiles</h2>
          <button
            className="import-button hover-scale btn-press"
            onClick={handleImport}
            title="Import Profile"
          >
            <Upload size={20} />
          </button>
        </div>

        <div className="profile-manager-body">
          {profiles.length === 0 ? (
            <div className="no-profiles">
              <p className="no-profiles-icon">👤</p>
              <p className="no-profiles-text">No profiles yet</p>
              <p className="no-profiles-hint">Create your first profile to get started!</p>
            </div>
          ) : (
            <div className="profiles-list">
              {profiles.map((profile, index) => (
                <div
                  key={profile.id}
                  className="profile-manager-card animate-slideInLeft"
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    borderLeft: `4px solid ${profile.color}`
                  }}
                >
                  <div className="profile-manager-avatar" style={{ background: profile.color }}>
                    <span>{profile.avatar}</span>
                  </div>

                  <div className="profile-manager-info">
                    <h3>{profile.name}</h3>
                    <p className="profile-manager-age">Age {profile.age}</p>
                    <div className="profile-manager-stats">
                      <span>🏆 {profile.earnedBadges?.length || 0} badges</span>
                      <span>📝 {profile.stats?.totalQuizzes || 0} quizzes</span>
                      <span>⭐ {profile.stats?.totalScore || 0} points</span>
                    </div>
                    <p className="profile-manager-date">
                      Created: {new Date(profile.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="profile-manager-actions">
                    <button
                      className="profile-action-btn edit-btn hover-scale btn-press"
                      onClick={() => handleEdit(profile)}
                      title="Edit Profile"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      className="profile-action-btn export-btn hover-scale btn-press"
                      onClick={() => handleExport(profile)}
                      title="Export Profile"
                    >
                      <Download size={18} />
                    </button>
                    <button
                      className="profile-action-btn delete-btn hover-scale btn-press"
                      onClick={() => handleDelete(profile)}
                      title="Delete Profile"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="profile-manager-footer">
          <p>💡 Tip: Export profiles to backup your progress!</p>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deletingProfile && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div
            className="modal-content delete-modal animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-modal-icon">
              <AlertTriangle size={48} color="#e74c3c" />
            </div>
            <h3>Delete Profile?</h3>
            <p>
              Are you sure you want to delete <strong>{deletingProfile.name}</strong>'s profile?
            </p>
            <p className="delete-warning">
              This will permanently delete all progress, badges, and quiz history.
              This action cannot be undone.
            </p>
            <div className="delete-modal-actions">
              <button
                className="cancel-delete-button hover-lift btn-press"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                className="confirm-delete-button hover-lift btn-press"
                onClick={confirmDelete}
              >
                <Trash2 size={18} />
                Delete Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileManager;
